import React, { useState, useEffect, useContext, useMemo } from "react";
import { Container, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import styles from "../../AmbulatoryVisit/css/ambulatoryVisit.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { BreadcrumbContext } from "Layouts/Header";
import { formatDate, showToastMessage } from "common/service/CommonService";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "Components/Common/Commonbadge";
import CommonButton from "Components/Common/CommonButton";
import { validations } from "validations/FormValidations";
import FullPageLoader from "Components/Common/FullPageLoader";
import {
  getPhysicalExamFields,
  getLaboratoryExamFields,
} from "../helper/poststudyassessmentconstants";

const PostStudyAssessmentSubjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams<{ subjectId: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const [isSubmittingVitals, setIsSubmittingVitals] = useState(false);
  const [isSubmittingLab, setIsSubmittingLab] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [vitalsSaved, setVitalsSaved] = useState(false);
  const [labSaved, setLabSaved] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingPage(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Post Study Assessment", link: "/study/detail/poststudyassessment" },
      { text: `Subject ${subjectId || ""} Assessment` },
    ]);

    return () => {
      breadcrumbContext?.setBreadcrumbItems?.([]);
    };
  }, [subjectId]);

  // Initialize useForm hook for CommonForm (No pre-filled values, keeping fields clean with placeholders)
  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      position: "",
      actualTime: "",
      bloodPressure: "",
      pulseRate: "",
      respirationRate: "",
      oralTemp: "",
      vitalWellBeing: "",

      respiratorySystem: "",
      gastroIntestinalSystem: "",
      cardiovascularSystem: "",
      centralNervousSystem: "",
      wellBeingAssessment: "",
      participantStatus: "",

      remarks: "",

      // Laboratory Assessment default values
      bloodSamplesCollected: "",
      bloodVolumeCollected: "",
      clinicallySignificantAbnormal: "",
      abnormalDetails: "",
    },
    mode: "onChange",
  });

  const clinicallySignificantAbnormal = watch("clinicallySignificantAbnormal");

  // Physical Examination / Vital Signs Fields Config (Imported from helper)
  const physicalExamFields = useMemo(() => getPhysicalExamFields(), []);

  // Laboratory Assessment Fields Config (Imported from helper, reactive on abnormal flag)
  const laboratoryExamFields = useMemo(
    () => getLaboratoryExamFields(clinicallySignificantAbnormal),
    [clinicallySignificantAbnormal]
  );

  // Separate save handler for Vital Signs Measurement card
  const handleSaveVitals = async () => {
    const fieldsToValidate = [
      "position",
      "actualTime",
      "bloodPressure",
      "pulseRate",
      "respirationRate",
      "oralTemp",
      "vitalWellBeing",
      "respiratorySystem",
      "gastroIntestinalSystem",
      "cardiovascularSystem",
      "centralNervousSystem",
      "wellBeingAssessment",
      "participantStatus",
    ];

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    setIsSubmittingVitals(true);
    setTimeout(() => {
      setIsSubmittingVitals(false);
      setVitalsSaved(true);
      showToastMessage("success", "Vital Signs Measurement saved successfully!");
    }, 600);
  };

  // Separate save handler for Laboratory Assessment card
  const handleSaveLab = async () => {
    const fieldsToValidate = [
      "bloodSamplesCollected",
      "clinicallySignificantAbnormal",
      ...(clinicallySignificantAbnormal === "Yes" ? ["abnormalDetails"] : []),
    ];

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    setIsSubmittingLab(true);
    setTimeout(() => {
      setIsSubmittingLab(false);
      setLabSaved(true);
      showToastMessage("success", "Laboratory Assessment saved successfully!");
    }, 600);
  };

  // Final Submit Handler
  const onSubmitFinalForm = (_data: any) => {
    setIsSubmittingFinal(true);
    setTimeout(() => {
      setIsSubmittingFinal(false);
      setVitalsSaved(true);
      setLabSaved(true);
      showToastMessage("success", "Post Study Assessment submitted successfully!");
      navigate("/study/detail/poststudyassessment");
    }, 600);
  };

  const isSubmitting = isSubmittingVitals || isSubmittingLab || isSubmittingFinal;

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      {(isLoadingPage || isSubmitting) && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 99999 }}>
          <FullPageLoader
            message={
              isSubmittingFinal
                ? "Submitting Post Study Assessment..."
                : isSubmittingVitals
                ? "Saving Vital Signs Measurement..."
                : isSubmittingLab
                ? "Saving Laboratory Assessment..."
                : "Loading Subject Assessment..."
            }
          />
        </div>
      )}
      <Container fluid>

        {/* 1. Subject Identification Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and post study assessment status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        {subjectId || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Assessment Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {formatDate(new Date())}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Status</div>
                    <Commonbadge status="checked in" style={{ width: "90px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 2. Vital Signs Measurement Card */}
        <SectionCard
          icon="ri-pulse-line"
          title="Vital Signs Measurement"
          subtitle="Physical examination, vital signs, system assessments, and participant status."
          rightContent={
            <Commonbadge
              status={vitalsSaved ? "completed" : "pending"}
              style={{ fontSize: "12px" }}
            />
          }
        >
          <CommonForm
            fields={physicalExamFields}
            control={control}
            errors={errors}
            columns={3}
            setValue={setValue}
            getValues={getValues}
          />
          <div className="d-flex justify-content-end mt-3">
            <CommonButton
              color="primary"
              size="sm"
              className="px-4 shadow-sm"
              onClick={handleSaveVitals}
              disabled={isSubmittingVitals}
            >
              {isSubmittingVitals ? (
                <>
                  <Spinner size="sm" className="me-1" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1" /> Save
                </>
              )}
            </CommonButton>
          </div>
        </SectionCard>

        {/* 3. Laboratory Assessment Card */}
        <SectionCard
          icon="ri-flask-line"
          title="Laboratory Assessment"
          subtitle="Record blood sample collection details and laboratory assessment findings."
          rightContent={
            <Commonbadge
              status={labSaved ? "completed" : "pending"}
              style={{ fontSize: "12px" }}
            />
          }
        >
          <CommonForm
            fields={laboratoryExamFields}
            control={control}
            errors={errors}
            columns={3}
            setValue={setValue}
            getValues={getValues}
          />
          <div className="d-flex justify-content-end mt-3">
            <CommonButton
              color="primary"
              size="sm"
              className="px-4 shadow-sm"
              onClick={handleSaveLab}
              disabled={isSubmittingLab}
            >
              {isSubmittingLab ? (
                <>
                  <Spinner size="sm" className="me-1" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1" /> Save 
                </>
              )}
            </CommonButton>
          </div>
        </SectionCard>

      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
          <div className={formStyles.footerActions}>
            <CommonButton
              type="button"
              color="dark"
              outline
              onClick={() => navigate("/study/detail/poststudyassessment")}
            >
              Cancel
            </CommonButton>
            <CommonButton
              color="primary"
              disabled={isSubmittingFinal}
              onClick={handleSubmit(onSubmitFinalForm)}
              className="fw-600"
            >
              {isSubmittingFinal ? (
                <>
                  <Spinner size="sm" className="me-2" /> Submitting...
                </>
              ) : (
                "Submit"
              )}
            </CommonButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostStudyAssessmentSubjectDetail;
